import {createSlice, createSelector, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { IReportDto } from './Report.model'
import { reportApi } from './Report.api'
import { signOut } from '../auth/Auth.slice'
import { TimeRangeItem } from '../../util/Constants'
import { TimeRange } from '../../common/component/DateRangeSelector'
import { recalibrateTimeRange } from '../../util/TimeRange.util'

export type SerializableTimeRange = {
  timeRangeItem: TimeRangeItem
  from: number
  to: number
}

type InitialState = {
  timeRangeSelection: SerializableTimeRange
  filtersSelection: IFilterDto[]
  reports: {
    error: string | undefined
    loading: boolean
    data: IReportDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  report: {
    error: string | undefined
    loading: boolean
    data: IReportDto | undefined
  }
}
const initialState = {
  timeRangeSelection: {
    timeRangeItem: {code: '30d', name: 'ÚLTIMOS 30 DÍAS', metric: 'D' as const, value: 30},
    from: Date.now() - 30 * 24 * 60 * 60 * 1000,
    to: Date.now(),
  },
  filtersSelection: [],
  reports: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 0,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  report: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setReportTimeRangeSelection: (state, action: PayloadAction<SerializableTimeRange>) => {
      state.timeRangeSelection = action.payload
    },
    setReportFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signOut, () => initialState)
      //Search
      .addMatcher(reportApi.endpoints.reportSearch.matchPending, (state) => {
        state.reports = {
          ...initialState.reports,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(reportApi.endpoints.reportSearch.matchFulfilled, (state, action) => {
        state.reports = {
          error: undefined,
          loading: false,
          data: action.payload.data,
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(reportApi.endpoints.reportSearch.matchRejected, (state, action) => {
        state.reports = {
          ...initialState.reports,
          error: action.error.message,
          loading: false,
        }
      })
      //LoadMore
      .addMatcher(reportApi.endpoints.reportLoadMore.matchPending, (state) => {
        state.reports.error = undefined
        state.reports.loading = true
      })
      .addMatcher(reportApi.endpoints.reportLoadMore.matchFulfilled, (state, action) => {
        state.reports.data.push(...action.payload.data)
        state.reports = {
          ...state.reports,
          error: undefined,
          loading: false,
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(reportApi.endpoints.reportLoadMore.matchRejected, (state, action) => {
        state.reports = {
          ...state.reports,
          error: action.error.message,
          loading: false,
        }
      })
      //GetById
      .addMatcher(reportApi.endpoints.reportGetById.matchPending, (state) => {
        state.report = {
          ...initialState.report,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(reportApi.endpoints.reportGetById.matchFulfilled, (state, action) => {
        state.report.data = action.payload
        state.report = {
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(reportApi.endpoints.reportGetById.matchRejected, (state, action) => {
        state.report = {
          ...initialState.report,
          error: action.error.message,
          loading: false,
        }
      })
      //Create
      .addMatcher(reportApi.endpoints.reportCreate.matchPending, (state) => {
        state.report = {
          ...initialState.report,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(reportApi.endpoints.reportCreate.matchFulfilled, (state, action) => {
        state.report = {
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(reportApi.endpoints.reportCreate.matchRejected, (state, action) => {
        state.report = {
          ...initialState.report,
          error: action.error.message,
          loading: false,
        }
      })
      //DeleteById
      .addMatcher(reportApi.endpoints.reportDeleteById.matchPending, (state) => {
        return {
          ...state,
          report: {
            ...initialState.report,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(reportApi.endpoints.reportDeleteById.matchFulfilled, (state, action) => {
        return {
          ...state,
          report: {
            ...state.report,
            error: undefined,
            loading: false,
          },
        }
      })
      .addMatcher(reportApi.endpoints.reportDeleteById.matchRejected, (state, action) => {
        return {
          ...state,
          report: {
            ...state.report,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //UpdateById
      .addMatcher(reportApi.endpoints.reportUpdateById.matchPending, (state) => {
        return {
          ...state,
          report: {
            ...state.report,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(reportApi.endpoints.reportUpdateById.matchFulfilled, (state, action) => {
        return {
          ...state,
          report: {
            ...state.report,
            error: undefined,
            loading: false,
            data: action.payload
          }
        }
      })
      .addMatcher(reportApi.endpoints.reportUpdateById.matchRejected, (state, action) => {
        return {
          ...state,
          report: {
            ...state.report,
            error: action.error.message,
            loading: false,
          },
        }
      })
  },
})

export const {setReportTimeRangeSelection, setReportFiltersSelection} = reportSlice.actions
export default reportSlice.reducer
export const selectReports = (state: RootState) => state.reportReducer.reports.data
export const selectReportsLoading = (state: RootState) => state.reportReducer.reports.loading
export const selectReportsPagination = (state: RootState) => state.reportReducer.reports.pagination
export const selectReportsFilters = (state: RootState) => state.reportReducer.reports.filters

export const selectReport = (state: RootState) => state.reportReducer.report.data
export const selectReportLoading = (state: RootState) => state.reportReducer.report.loading
export const selectReportFiltersSelection = (state: RootState) => state.reportReducer.filtersSelection

const selectReportTimeRangeSelectionState = (state: RootState) => state.reportReducer.timeRangeSelection
export const selectReportTimeRangeSelection = createSelector(
  [selectReportTimeRangeSelectionState],
  (timeRangeSelection): TimeRange => recalibrateTimeRange(timeRangeSelection)
)
